'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, LogIn, UserPlus, MessageCircle, Home, ChevronDown, LogOut, Settings, User, LayoutDashboard, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage, SUPPORTED_LANGUAGES } from '@/lib/i18n';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';


export default function Topbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const pathname = usePathname();
  const { user, signOut, loading } = useAuth();
  const { language, setLanguage, t, isRTL } = useLanguage();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Check admin role from DB
  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }
    const supabase = createClient();
    supabase
      .from('user_profiles')
      .select('role')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        setIsAdmin(data?.role === 'admin');
      });
  }, [user]);

  const isAuthenticated = !!user;
  const userName = user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'Compte';

  const navLinks = [
    { href: '/home-prompts-screen', label: t.nav.home, icon: Home },
    { href: '/ai-chat-interface?new=1', label: t.nav.aiAssistant, icon: MessageCircle, requiresAuth: true },
    { href: '/admin', label: 'Admin', icon: Shield, requiresAuth: true, requiresAdmin: true },
    { href: '/dashboard', label: t.nav.dashboard, icon: LayoutDashboard, requiresAuth: true },
  ];

  const activeLang = language;

  // Close user menu on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    try {
      await signOut();
    } catch (e) {
      // ignore
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-100 shadow-sm" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/home-prompts-screen" className="flex items-center gap-2.5 group">
            <AppLogo
              src="/assets/images/dalili-1775932023691.png"
              size={36}
              className="transition-transform duration-200 group-hover:scale-105"
            />
            <span className="font-jakarta font-700 text-xl text-primary-700 tracking-tight">
              Dalili
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks?.map((link) => {
              const isActive = mounted && pathname === link?.href;
              const Icon = link?.icon;
              if (link?.requiresAuth && !isAuthenticated) return null;
              if (link?.requiresAdmin && !isAdmin) return null;
              return (
                <Link
                  key={`nav-${link?.href}`}
                  href={link?.href}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50 hover:text-primary-700'
                  }`}
                >
                  <Icon size={16} />
                  {link?.label}
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-2">
            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all duration-150"
              >
                <span>{SUPPORTED_LANGUAGES?.find((l) => l?.code === activeLang)?.flag}</span>
                <span>{SUPPORTED_LANGUAGES?.find((l) => l?.code === activeLang)?.label}</span>
                <ChevronDown size={14} className={`transition-transform duration-150 ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-1 w-40 bg-white rounded-xl shadow-card-lg border border-gray-100 py-1 z-50 animate-fade-in`}>
                  {SUPPORTED_LANGUAGES?.map((lang) => (
                    <button
                      key={`lang-${lang?.code}`}
                      onClick={() => { setLanguage(lang?.code); setLangOpen(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors duration-100 ${
                        activeLang === lang?.code
                          ? 'text-primary-700 bg-primary-50' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span>{lang?.flag}</span>
                      <span>{lang?.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {!loading && (
              isAuthenticated ? (
                <div className="flex items-center gap-2">
                  {/* User icon dropdown */}
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-50 text-primary-700 hover:bg-primary-100 transition-all duration-150 border border-primary-100"
                      title={userName}
                      aria-label="Menu utilisateur"
                    >
                      <User size={18} />
                    </button>

                    {userMenuOpen && (
                      <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50 animate-fade-in`}>
                        {/* Username */}
                        <div className="px-4 py-2.5 border-b border-gray-100">
                          <p className="text-xs text-gray-400 mb-0.5">{t.nav.loggedInAs}</p>
                          <p className="text-sm font-semibold text-gray-800 truncate">{userName}</p>
                        </div>

                        {/* Profile / Settings link */}
                        <Link
                          href="/user-settings"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-700 transition-colors duration-100"
                        >
                          <Settings size={15} />
                          {t.nav.profile}
                        </Link>

                        {/* Logout */}
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors duration-100"
                        >
                          <LogOut size={15} />
                          {t.nav.logout}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    href="/sign-up-login-screen"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all duration-150"
                  >
                    <LogIn size={15} />
                    {t.nav.login}
                  </Link>
                  <Link
                    href="/sign-up-login-screen"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-primary-700 text-white hover:bg-primary-800 transition-all duration-150 active:scale-95 shadow-sm"
                  >
                    <UserPlus size={15} />
                    {t.nav.register}
                  </Link>
                </div>
              )
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white animate-slide-up">
          <div className="px-4 py-3 space-y-1">
            {navLinks?.map((link) => {
              if (link?.requiresAuth && !isAuthenticated) return null;
              if (link?.requiresAdmin && !isAdmin) return null;
              const Icon = link?.icon;
              return (
                <Link
                  key={`mobile-nav-${link?.href}`}
                  href={link?.href}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <Icon size={16} />
                  {link?.label}
                </Link>
              );
            })}
            <div className="pt-2 border-t border-gray-100 space-y-1">
              {/* Mobile language switcher */}
              <div className="px-3 py-2">
                <p className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Langue / اللغة</p>
                <div className="flex gap-2">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={`mobile-lang-${lang.code}`}
                      onClick={() => { setLanguage(lang.code); setMobileOpen(false); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        activeLang === lang.code
                          ? 'bg-primary-700 text-white' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {!loading && (
                isAuthenticated ? (
                  <>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-50 text-primary-700 border border-primary-100 flex-shrink-0">
                        <User size={16} />
                      </div>
                      <span className="text-sm font-medium text-gray-800 truncate">{userName}</span>
                    </div>
                    <Link
                      href="/user-settings"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                      onClick={() => setMobileOpen(false)}
                    >
                      <Settings size={16} />
                      {t.nav.profile}
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setMobileOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
                    >
                      <LogOut size={16} />
                      {t.nav.logout}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      href="/sign-up-login-screen"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                      onClick={() => setMobileOpen(false)}
                    >
                      <LogIn size={16} />
                      {t.nav.login}
                    </Link>
                    <Link
                      href="/sign-up-login-screen"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium bg-primary-700 text-white hover:bg-primary-800"
                      onClick={() => setMobileOpen(false)}
                    >
                      <UserPlus size={16} />
                      {t.nav.register}
                    </Link>
                  </>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}